// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

import type { SharedVaultKeyContribution } from '@cobault/key-agent';
import {
  api,
  type AcceptSharedVaultRequest,
  type AcceptSharedVaultResponse,
  type SharedVaultDetailResponse,
  type SharedVaultParticipant,
} from './api.js';
import type { KeyAgentClient } from './key-agent-loader.js';
import { PLATFORM_TERMS_VERSION } from './pages/platform-terms.js';

/** The one key-agent capability the accept flow needs — narrowed so tests can
 *  hand in a stub without faking the whole client. */
export type SharedVaultKeyContributor = Pick<
  KeyAgentClient,
  'contributeSharedVaultKey'
>;

/** The two-step shared-vault inception ceremony, stated up front so every
 *  co-owner knows the second step is coming *before* they take the first
 *  (COB-383). One exported constant renders across the SPA inception
 *  surfaces — the claim-link landing and the creation wizard's first step — so
 *  their copy can't drift. (COB-543 retired the third, the in-app
 *  pending-invitee section, along with the tokenless accept it offered.)
 *
 *  Flavour-neutral by design: it reads correctly for the initiator and for
 *  every invitee, since all of them complete both steps. Canonical verbs
 *  (COB-486: **Consent** for phase A — `POST /shared-vault/{id}/accept`;
 *  **Sign to activate the vault** for phase B — `POST …/acknowledge`);
 *  only the copy uses these verbs, the code identifiers keep their names.
 *
 *  COB-512 retired phase B's old verb, "Sign to accept the vault terms". That
 *  signature is over the **activation manifest** — the vault's descriptor,
 *  roster and quorum, re-derived in the signer's own browser — and not over
 *  any legal document. The old wording read as though the user had signed
 *  something they were never shown, which became actively misleading once the
 *  Platform Terms acquired a real, separately recorded acceptance on the
 *  consent step. The verb now names what is actually signed. */
export const SHARED_VAULT_TWO_STEP_NOTE =
  'Every co-owner completes two steps: first Consent to participation in the ' +
  'vault, then — once every participant has consented — Sign to activate the ' +
  "vault, confirming the vault's own setup, before it can receive deposits.";

/** Assemble the `POST /shared-vault/{id}/accept` body from the key-agent's PoP
 *  contribution plus the accept credential: the raw bearer token lifted from
 *  the claim link's fragment. A token rides in the body, never the URL.
 *
 *  COB-543: the token is REQUIRED. The tokenless in-app path (COB-337) matched
 *  the caller's server-verified account email against an `INVITED` slot — there
 *  are no invited addresses to match any more, so presenting the token is the
 *  only way a slot is ever claimed. */
export function buildAcceptSharedVaultRequest(
  inviteToken: string,
  contribution: SharedVaultKeyContribution,
): AcceptSharedVaultRequest {
  return {
    invite_token: inviteToken,
    multisig_pubkey_hex: contribution.multisig_pubkey_hex,
    derivation_index: contribution.derivation_index,
    // HW.13a: the account key-origin for the registration record; absent ⇒
    // the server keeps no registration row for this contributor.
    key_origin: contribution.account_key_origin,
    // WS-H v2 proof (HW.8): `der-sha256-v1` (software) / `bsm-bip137-v1` (device).
    pop_proof: contribution.pop_proof,
    // COB-512: the version of the Platform Terms the claim page rendered. Taken
    // from the published page's own constant, never a literal here, so what is
    // recorded is always the version the user was actually shown.
    platform_terms_version: PLATFORM_TERMS_VERSION,
  };
}

/** The claim flow behind the claim-link landing (`shared-vault-accept.tsx`):
 *  derive a fresh per-vault multisig key + PoP in the browser, bound to the
 *  slot the token resolved to (D1), then POST the accept. The backend verifies
 *  the PoP *before* consuming the slot, so a failure here never burns the
 *  invitation. */
export async function contributeAndAcceptSharedVault(
  agent: SharedVaultKeyContributor,
  vaultId: string,
  slotId: string,
  inviteToken: string,
): Promise<AcceptSharedVaultResponse> {
  const contribution = await agent.contributeSharedVaultKey(vaultId, slotId);
  return api.acceptSharedVault(
    vaultId,
    buildAcceptSharedVaultRequest(inviteToken, contribution),
  );
}

/** COB-338: did an accept COMMIT even though its POST errored? The server
 *  claims the slot atomically before sealing, so a response lost in transit
 *  leaves the caller accepted with a dead credential — the claim page re-reads
 *  the vault on error and checks the claimed slot's row before reporting
 *  failure (an ACCEPTED own slot means "you're in", not "retry"). Doubly worth
 *  it since COB-543: the burnt token is single-use and unrecoverable, so a
 *  spurious "retry" would strand the claimant until the initiator regenerates. */
export function acceptCommittedDespiteError(
  participants: SharedVaultParticipant[],
  slotId: string,
): boolean {
  return participants.some(
    (p) => p.slot_id === slotId && p.status === 'ACCEPTED',
  );
}

/** COB-338: every slot is claimed but the vault has not sealed to `PROPOSED`
 *  yet — the roster-filling accept committed while its server-side seal
 *  failed. The backend's lazy reconcile re-runs the seal on each detail read
 *  (the detail page polls every 5s), so this state normally clears itself in
 *  seconds; render it as "finishing activation", never as an error. */
export function activationFinishing(vault: SharedVaultDetailResponse): boolean {
  return (
    vault.state === 'PENDING_INVITES' &&
    vault.participants.length > 0 &&
    vault.participants.every((p) => p.status !== 'INVITED') &&
    vault.participants.some((p) => p.status === 'ACCEPTED')
  );
}
